/* eslint-disable max-lines */
import parse from "html-react-parser"
import QueryString from "qs"
import _slugify from "slugify"

export const scrollToElement = (element, offset = 1) => {
  const bodyRect = document.body.getBoundingClientRect().top
  const elementRect = element.getBoundingClientRect().top
  const elementPosition = elementRect - bodyRect
  const offsetPosition = elementPosition - offset

  typeof window !== "undefined" &&
    window.scrollTo({
      top: offsetPosition,
      behavior: "smooth",
    })
}

export const scrollToID = (id, offset = 1) => {
  const element = document.getElementById(id)

  if (element) {
    scrollToElement(element, offset)
  }
}

export const getMenuLink = (menu) => {
  let link = {}

  if (menu) {
    const { slug, custom_link, external_link, strapi_parent } = menu
    const formattedCustomLink = custom_link?.replace(/ /g, "")

    if (formattedCustomLink) {
      if (formattedCustomLink.includes("http") || external_link) {
        link = {
          href: formattedCustomLink,
          target: "_blank",
        }
      } else {
        link = {
          to: formattedCustomLink,
        }
      }
    } else if (slug) {
      // TODO: add recursive function to get parent slugs
      if (strapi_parent) {
        link = {
          to: `/${strapi_parent.slug}/${slug}/`,
        }
      } else {
        link = {
          to: `/${slug}/`,
        }
      }
    }
  }

  return link
}

export const parseHTML = (htmlString) => {
  if (typeof htmlString === "string" && htmlString?.length > 0) {
    return parse(htmlString)
  }

  return null
}

export const stripHTML = (htmlString) => {
  if (typeof htmlString === "string" && htmlString?.length > 0) {
    return htmlString.replace(/(<([^>]+)>)/gi, "")
  }

  return null
}

export const getIconColor = (color, defaultColor) => {
  let stroke = defaultColor || "#fff"

  if (color?.[0] === "#") return color

  switch (color) {
    case "white":
      stroke = "#fff"
      break
    case "red":
      stroke = "red"
      break
    case "primary":
      stroke = "#111A2F"
      break
    case "secondary":
      stroke = "#4B504F"
      break
    case "gray":
      stroke = "#E6E6E6"
      break
    default:
      stroke = defaultColor || "#fff"
      break
  }

  return stroke
}

export const getWhatsAppURL = (number, message) => {
  if (!number) return null
  // remove everything except numbers
  const formatedNumber = number?.replace(/\D/g, "")

  const query = QueryString.stringify(
    {
      phone: formatedNumber,
      text: message,
    },
    {
      encodeValuesOnly: true, // prettify URL
    },
  )

  return `https://api.whatsapp.com/send?${query}`
}

export const formatNumber = (number) => {
  if (number || number === 0) {
    return Number(number.toString()?.replace(/[^\d.]/g, "")).toLocaleString(
      "en-US",
    )
  }

  return null
}

const filterNumber = (str) => {
  if (!str) return 0
  const myStr = str.toString()
  return parseInt(myStr.replace(/[^\d.]/g, ""), 10)
}

export const currencyFormat = (num, currency = "AED ", withSpace = false) => {
  const filterNum = filterNumber(num)
  if (!filterNum) {
    return `${currency} 0`
  }
  if (withSpace) {
    return `${currency} ${new Intl.NumberFormat("en-US", {}).format(filterNum)}`
  }
  return `${currency}${new Intl.NumberFormat("en-US", {}).format(filterNum)}`
}

export const calculateMonthlyPayment = ({
  price,
  downPaymentPercentage,
  downPayment,
  interestRateYearly,
  loanPeriodYearly,
}) => {
  let downPaymentAmount = downPayment
  downPaymentAmount ||=
    parseInt(price, 10) * (parseInt(downPaymentPercentage, 10) / 100)

  const loanAmount = parseInt(price, 10) - (downPaymentAmount || 0)
  const monthlyInterestRate = parseFloat(interestRateYearly) / (12 * 100)
  const loanPeriodInMonths = parseInt(loanPeriodYearly, 10) * 12

  if (loanAmount <= 0 || loanPeriodInMonths <= 0) {
    return 0
  }

  const monthlyPayment =
    (loanAmount * monthlyInterestRate) /
    (1 - (1 + monthlyInterestRate) ** -loanPeriodInMonths)

  if (
    Number.isNaN(monthlyPayment) ||
    monthlyPayment === Infinity ||
    monthlyPayment === -Infinity ||
    monthlyPayment < 0
  ) {
    return 0
  }

  return monthlyPayment
}

// TODO: make this recursive
export const getBreadcrumbUrls = (menuData) => {
  const urls = []
  const current = menuData
  if (current?.strapi_parent?.strapi_parent) {
    urls.push({
      label: current.strapi_parent.strapi_parent.title,
      url: `/${current.strapi_parent.strapi_parent.slug}/`,
    })
    urls.push({
      label: current.strapi_parent.title,
      url: `/${current.strapi_parent.strapi_parent.slug}/${current.strapi_parent.slug}/`,
    })
    urls.push({
      label: current.title,
      url: `/${current.strapi_parent.strapi_parent.slug}/${current.strapi_parent.slug}/${current.slug}/`,
    })
  } else if (current?.strapi_parent) {
    urls.push({
      label: current.strapi_parent.title,
      url: `/${current.strapi_parent.slug}/`,
    })
    urls.push({
      label: current.title,
      url: `/${current.strapi_parent.slug}/${current.slug}/`,
    })
  } else {
    urls.push({
      label: current?.title,
      url: `/${current?.slug}/`,
    })
  }
  return urls
}

export const trackShare = (event, shareURL) => {
  window.dataLayer = window.dataLayer || []
  window.dataLayer.push({
    event: "Share - social",
    formType: `${event} - ${shareURL}`,
    formId: "Share",
    formName: "Share",
    formLabel: "Share",
  })
}

export const abbreviateNumber = (value) => {
  const formatter = new Intl.NumberFormat("en-US", {
    notation: "compact",
    compactDisplay: "short",
    maximumFractionDigits: 1,
  })
  const newValue = formatter.format(value).toLocaleLowerCase()
  return newValue
}

export const slugToTitle = (
  slug,
  { capitalize } = {
    capitalize: true,
  },
) => {
  if (!slug || typeof slug !== "string") return null

  const title = slug
    .split("-")
    .map((word) => {
      if (capitalize) {
        return word.charAt(0).toUpperCase() + word.slice(1)
      }
      return word
    })
    .join(" ")
  return title
}

export const sort = (a, b, sortType = "asc") => {
  if (a === null || a === undefined) {
    return 1
  }
  if (b === null || b === undefined) {
    return -1
  }
  if (sortType === "asc") {
    return a - b
  }
  return b - a
}

export const slugify = (text) => {
  if (!text) return ""

  let toSlug = text

  if (typeof text !== "string") {
    toSlug = text.toString()
  }

  if (typeof toSlug !== "string") return ""

  return _slugify(toSlug, { lower: true, trim: true, strict: true })
}

export function gtagReportConversion(url) {
  const callback = () => {
    if (typeof url !== "undefined") {
      // window.location = url
    }
  }

  typeof window !== "undefined" &&
    window.dataLayer?.push("event", "conversion", {
      send_to: "G-VTTZT0CG7Y/dvs1CK7Pi-gYENiv0uMC",
      event_callback: callback,
      value: url,
    })
  return false
}
